import React from "react";
import _ from "lodash";
import classNames from "classnames";
import styles from "./Table.module.scss";
import loadingStyles from "./TableLoading.module.scss";
import { extractValue } from "./useTable";
import { UnmountClosed } from "react-collapse";
import DefaultCellRenderer from "./DefaultCellRenderer";
import BlankRow from "./BlankRow";

export default React.memo(({    columns,
                                rows,
                                selectionState,
                                rowClassName,
                                cellClassName,
                                widths,
                                isRowSelectingEnabled,
                                isMultiSelect,
                                toggleRowSelection,
                                rowState,
                                updateRowState,
                                onRowClick,
                                onRowDoubleClick,
                                lastColumnSticky,
                                lastColumnStickyStyleRef,
                                stickColumnIndex,
                                stickyColumnStyleRef,
                                SubRowRenderer,
                                isRowDisabled,
                                disabledTitle,
                                innerTable,
                                tableWidth,
                                isLoading
                            }) => {
    function rowRenderFn(row, rowId, rowState, isRowSelected) {
        const updateRow = updateRowState && updateRowState(rowId);
        const selectRow = isRowSelectingEnabled && toggleRowSelection(rowId);
        const disabled = row.isDisabled || (_.isFunction(isRowDisabled) ? isRowDisabled(row) : false);
        const onClick = onRowClick && ((e) => onRowClick(row, e));
        const onDoubleClick = onRowDoubleClick && (() => onRowDoubleClick(row));
        const isSelected = rowState?.isSelected;
        const isExpanded = rowState?.isExpanded;
        return (
            <React.Fragment key={rowId}>
                <BlankRow
                    stickColumnIndex={stickColumnIndex}
                    stickyColumnStyleRef={stickyColumnStyleRef}
                    columns={columns}
                    widths={widths}
                />

                <>
                    <div title={disabled ? disabledTitle : undefined} className={classNames(styles['row'], rowClassName, { [styles['selected']]: isSelected, [styles['expanded']]: isExpanded, [styles['clickable']]: !!onClick, [styles['disabled-row']]: disabled })} onClick={onClick} onDoubleClick={onDoubleClick} style={{ width: "100%" }} data-testid={`table-row-${rowId}`}>
                        {columns.map((column, index) => {
                            const stickCustomColumn = index <= stickColumnIndex;
                            const stickLastColumn = lastColumnSticky && index === columns.length - 1;
                            const className = classNames(styles['cell'], column.cellClass, cellClassName, { [lastColumnStickyStyleRef?.current.dataset.styleid]: stickLastColumn, [styles['disabled-cell']]: disabled, [stickyColumnStyleRef?.current.dataset.styleid]: stickCustomColumn, [styles['inner-table-cell']]: innerTable });
                            const CellRenderer = column.Renderer || DefaultCellRenderer;
                            const value = extractValue(row, column.accessor);
                            const additionalStyles = column.cellStyle ? column.cellStyle({ value, row, rowState, isRowSelected }) : undefined;
                            const style = {
                                flex: column.fixedWidth ? `0 0 ${widths[index]}px` : `1 0 ${widths[index]}px`,
                                ...additionalStyles
                            };

                            return <div key={column.id} className={className} style={style} data-testid={`table-cell-column-${column.header === "" ? column.id : column.header}-row-${rowId}`}>
                                <CellRenderer
                                    row={row}
                                    value={value}
                                    column={column}
                                    isMultiSelect={isMultiSelect}
                                    isRowSelected={isRowSelected}
                                    updateRow={updateRow}
                                    selectRow={selectRow}
                                    rowState={rowState}
                                    disabled={disabled}
                                />
                            </div>
                        })}
                    </div>
                    {SubRowRenderer && <UnmountClosed isOpened={isExpanded} theme={{
                        collapse: styles['sub-row-transition-parent'],
                        content: classNames(styles['sub-row'], stickyColumnStyleRef.current.dataset.styleid)
                    }}>
                        <SubRowRenderer row={row} rowState={rowState} updateRow={updateRow} />
                    </UnmountClosed>}
                </>
            </React.Fragment>
        );
    }

    if (isLoading) {
        return _.range(columns.length).map((row) => {
            return (
                <div key={row} className={loadingStyles["loading-row"]}>
                    {_.range(columns.length).map((column) => {
                        const style = { flex: `1 0 ${widths[column]}px` };

                        const includeMask = _.sum(_.take(widths, column + 1)) < tableWidth && column < columns.length;
                        return (
                            <div
                                style={style}
                                key={column}
                                className={classNames({
                                    [loadingStyles["loading-cell"]]: !includeMask,
                                    [loadingStyles["loading-cell-with-mask"]]: includeMask,
                                })}
                            />
                        );
                    })}
                </div>
            );
        });
    }
    return rows.map((row, index) => {
        const rowId = row.id;
        const currentRowState = (rowState && rowState[rowId]) || {};
        const isRowSelected = _.get(selectionState, `rows[${rowId}]`, false);

        return rowRenderFn(row, rowId, currentRowState, isRowSelected);
    });
});
